<template>
  <div>
    <v-btn
      :disabled="disabled || exporting"
      color="primary"
      :loading="exporting"
      @click="initiateExport"
      text
      class="font-weight-black text-uppercase tracking-widest"
    >
      <v-icon class="mr-2" small>fas fa-file-export</v-icon>
      Exportar
    </v-btn>

    <v-dialog v-model="exportDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="px-6 py-4">
          <v-icon class="mr-3" color="primary">fas fa-file-excel</v-icon>
          <span class="text-h6 font-weight-bold">Exportando Inventario</span>
        </v-card-title>

        <v-divider />

        <v-card-text class="px-6 py-6">
          <div class="text-center mb-4">
            <v-progress-circular
              :value="exportProgress"
              :size="80"
              :width="8"
              color="primary"
              class="mb-4"
            >
              {{ exportProgress }}%
            </v-progress-circular>
          </div>

          <div class="text-center">
            <div class="subtitle-1 font-weight-medium mb-2">{{ exportStatus }}</div>
            <div class="caption grey--text">{{ exportMessage }}</div>
          </div>

          <v-alert
            v-if="exportError"
            type="error"
            dense
            class="mt-4"
          >
            {{ exportError }}
          </v-alert>
        </v-card-text>

        <v-divider />

        <v-card-actions class="px-6 py-4">
          <v-spacer />
          <v-btn
            v-if="exportStatus === 'error' || exportStatus === 'completed'"
            text
            @click="closeDialog"
          >
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import notifications from '@/utils/notifications'
import { initiateStockExport, getStockExportStatus, downloadStockExport } from '@/api/stocks'
import moment from 'moment'

export default {
  name: "ExportStock",
  props: {
    family: { type: [String, Number], default: '' },
    store: { type: [String, Number], default: '' },
    isCritical: { default: null },
    search: { type: String, default: '' },
    disabled: { type: Boolean, default: false }
  },

  data() {
    return {
      exporting: false,
      exportDialog: false,
      exportStatus: 'pending',
      exportProgress: 0,
      exportMessage: '',
      exportError: null,
      taskId: null,
      pollingInterval: null,
    }
  },

  methods: {
    async initiateExport() {
      this.exporting = true
      this.exportDialog = true
      this.exportStatus = 'pending'
      this.exportProgress = 0
      this.exportMessage = 'Iniciando exportación...'
      this.exportError = null

      console.log("Export isCritical", this.isCritical, "type:", typeof this.isCritical)

      try {
        // Iniciar la exportación en el backend
        const response = await initiateStockExport({
          family: this.family || null,
          store: this.store || null,
          is_critical: (this.isCritical === '' || this.isCritical === null || this.isCritical === undefined) ? null : this.isCritical,
          search: (this.search || '').trim()
        })

        this.taskId = response.task_id
        this.exportMessage = 'Tarea iniciada, generando reporte...'

        // Iniciar polling para verificar el estado
        this.startPolling()

      } catch (error) {
        this.exportError = 'Error al iniciar la exportación: ' + error.message
        this.exportStatus = 'error'
        this.exporting = false
      }
    },

    startPolling() {
      // Polling cada 2 segundos
      this.pollingInterval = setInterval(async () => {
        try {
          const status = await getStockExportStatus(this.taskId)

          this.exportStatus = status.status
          this.exportProgress = status.progress || 0
          this.exportMessage = status.message || ''

          // Si completó, descargar el archivo
          if (status.status === 'completed') {
            this.stopPolling()
            await this.downloadFile()
          }

          // Si hubo error, detener polling
          if (status.status === 'error') {
            this.stopPolling()
            this.exportError = status.message || 'Error al generar el archivo'
            this.exporting = false
          }

        } catch (error) {
          this.stopPolling()
          this.exportError = 'Error al verificar el estado: ' + error.message
          this.exportStatus = 'error'
          this.exporting = false
        }
      }, 2000) // Polling cada 2 segundos
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
    },

    async downloadFile() {
      try {
        this.exportMessage = 'Descargando archivo...'

        const fileData = await downloadStockExport(this.taskId)

        // Crear link de descarga
        const fileName = `inventario_${moment().format('DD-MM-YYYY_HHmm')}.xlsx`
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(fileData)
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        notifications.showSuccess('Inventario exportado exitosamente')
        this.exportProgress = 100
        this.exportMessage = 'Descarga completada'

        // Cerrar el diálogo después de 1 segundo
        setTimeout(() => {
          this.closeDialog()
        }, 1000)

      } catch (error) {
        this.exportError = 'Error al descargar el archivo: ' + error.message
        this.exportStatus = 'error'
        notifications.showError('Error al descargar el archivo')
      } finally {
        this.exporting = false
      }
    },

    closeDialog() {
      this.exportDialog = false
      this.stopPolling()
      this.exporting = false
      this.exportStatus = 'pending'
      this.exportProgress = 0
      this.exportMessage = ''
      this.exportError = null
      this.taskId = null
    }
  },

  beforeDestroy() {
    // Limpiar el polling si el componente se destruye
    this.stopPolling()
  }
}
</script>

<style scoped>
.tracking-widest {
  letter-spacing: 1.5px;
}
</style>
